import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Footer } from '@/components/Footer';
import { Leaf, Shield, BookOpen, Users, ChevronRight, Star, Check, Clock, Award, Heart, Zap, ArrowRight, X, Sparkles, Gift } from 'lucide-react';

export const LandingPage = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [showOfferBar, setShowOfferBar] = useState(true);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: <Leaf className="w-6 h-6" strokeWidth={1.5} />,
      title: "Personalized Ayurvedic Assessment",
      description: "AI-powered analysis combining ancient wisdom with modern wellness interpretation"
    },
    {
      icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,
      title: "Safe Herbal Recommendations",
      description: "Evidence-based herbal remedies with proper warnings and guidelines"
    },
    {
      icon: <BookOpen className="w-6 h-6" strokeWidth={1.5} />,
      title: "Lifestyle Guidance",
      description: "Comprehensive do's and don'ts tailored to your unique constitution"
    },
    {
      icon: <Users className="w-6 h-6" strokeWidth={1.5} />,
      title: "Panchkarma Booking",
      description: "Connect with certified centers for authentic detox treatments"
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Mumbai",
      text: "The assessment was incredibly accurate. It aligned perfectly with what my local vaidya had told me.",
      rating: 5
    },
    {
      name: "Michael Chen",
      location: "Singapore",
      text: "Finally, an app that bridges traditional Ayurveda with modern medicine. Very comprehensive!",
      rating: 5
    },
    {
      name: "Sarah Williams",
      location: "London",
      text: "The lifestyle recommendations have transformed my daily routine. I feel more balanced than ever.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]">
      {/* Festive Offer Announcement Bar */}
      {showOfferBar && (
        <div className="offer-bar py-2.5 px-4 relative" data-testid="festive-offer-bar">
          {/* Sparkle decorations */}
          <div className="offer-bar-sparkle" style={{ top: '20%', left: '10%', animationDelay: '0s' }} />
          <div className="offer-bar-sparkle" style={{ top: '60%', left: '25%', animationDelay: '0.7s' }} />
          <div className="offer-bar-sparkle" style={{ top: '30%', right: '20%', animationDelay: '1.2s' }} />
          <div className="offer-bar-sparkle" style={{ top: '70%', right: '8%', animationDelay: '0.4s' }} />

          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 relative z-10">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#d4af37]" />
                <span className="text-[#d4af37]/80 text-sm font-medium tracking-wide uppercase hidden sm:inline">Limited Time Offer</span>
              </div>
              <div className="offer-divider hidden sm:block" />
              <span className="offer-percentage text-lg sm:text-xl font-black tracking-tight">95% OFF</span>
              <span className="text-white/90 text-sm font-medium">AI Assessment</span>
              <div className="offer-divider hidden sm:block" />
              <button
                onClick={() => {
                  login();
                }}
                className="offer-badge-glow bg-[#d4af37] text-[#1a0a00] text-xs font-bold px-3 py-1 rounded-full hover:bg-[#f5d060] transition-colors cursor-pointer"
                data-testid="offer-bar-cta"
              >
                Claim Now
              </button>
            </div>
            <button
              onClick={() => setShowOfferBar(false)}
              className="absolute right-0 text-white/40 hover:text-white/80 transition-colors p-1"
              data-testid="offer-bar-close"
              aria-label="Close offer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`glass fixed left-0 right-0 z-50 border-b border-stone-200/50 ${showOfferBar ? 'top-[44px]' : 'top-0'}`} style={{ transition: 'top 0.3s ease' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
              alt="Logo" 
              className="h-10 w-auto"
            />
            <span className="text-xl font-semibold text-[hsl(25,30%,35%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ayurveda Consult
            </span>
          </div>
          <Button 
            onClick={login}
            className="btn-primary"
            data-testid="header-login-btn"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`hero-bg pb-20 px-4 md:px-8 relative overflow-hidden ${showOfferBar ? 'pt-[140px]' : 'pt-32'}`} style={{ transition: 'padding-top 0.3s ease' }}>
        <div className="decorative-circle w-96 h-96 -top-20 -right-20" />
        <div className="decorative-circle w-64 h-64 bottom-0 left-10" />
        
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="animate-slide-up">
            
            <span className="badge-gold mb-6 inline-block">AI Powered, Expert Assisted</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[hsl(120,10%,18%)] mb-6 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ancient Wisdom,<br />
              <span className="text-[hsl(22,70%,44%)]">Modern Science</span>
            </h1>
            <p className="text-lg text-stone-600 mb-8 leading-relaxed max-w-xl">
              Get personalized Ayurvedic consultations powered by AI, complete with modern wellness interpretations. 
              Your path to holistic wellness starts here.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={login}
                className="btn-primary text-lg group"
                data-testid="hero-cta-btn"
              >
                Get Your Quick Assessment
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary text-lg"
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                data-testid="learn-more-btn"
              >
                See How It Works
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 md:gap-6 text-sm text-stone-500">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[hsl(135,15%,33%)]" />
                No subscription
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[hsl(135,15%,33%)]" />
                Results in 3 mins
              </span>
              <span className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[hsl(135,15%,33%)]" />
                Expert verified
              </span>
            </div>
            
            {/* Social Proof */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-xs text-stone-500">Trusted by 500+ users worldwide</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative animate-fade-in stagger-2">
            <img 
              src="https://images.unsplash.com/photo-1758274526671-ad18176acb01"
              alt="Meditation and wellness"
              className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[hsl(22,70%,44%)] rounded-full flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[hsl(120,10%,18%)]">5000+ Years</p>
                  <p className="text-sm text-stone-500">of Ayurvedic Wisdom</p>
                </div>
              </div>
            </div>
            
            {/* Floating testimonial card */}
            <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg max-w-[200px] hidden lg:block">
              <div className="flex items-center gap-1 mb-1">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-xs text-stone-600 italic">"Finally understood what it is to look after my health."</p>
              <p className="text-xs text-stone-400 mt-1">— Belinda M.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white" id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Comprehensive Wellness Journey
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              From assessment to lifestyle recommendations, we guide you through every step of your Ayurvedic wellness path.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card-elevated p-6 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-[hsl(22,70%,44%)/0.1] rounded-2xl flex items-center justify-center text-[hsl(22,70%,44%)] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {feature.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-12 bg-gradient-to-r from-stone-100 to-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(22,70%,44%)]">500+</div>
              <div className="text-sm text-stone-600">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(22,70%,44%)]">4.9/5</div>
              <div className="text-sm text-stone-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(22,70%,44%)]">3 min</div>
              <div className="text-sm text-stone-600">Assessment Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(22,70%,44%)]">24/7</div>
              <div className="text-sm text-stone-600">AI Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding" id="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              How It Works
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              A simple, guided process to receive your personalized Ayurvedic consultation
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Share Your Concerns", desc: "Tell us about your health and lifestyle concerns" },
              { step: "02", title: "Quick Payment", desc: "Secure one-time payment — AI Assessment just $1.49 USD" },
              { step: "03", title: "Answer Questions", desc: "Complete lifestyle and clarifying questionnaires" },
              { step: "04", title: "Get Assessment", desc: "Receive comprehensive assessment and recommendations" }
            ].map((item, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(43,70%,52%)/0.2] flex items-center justify-center">
                  <span className="text-2xl font-bold text-[hsl(22,70%,44%)]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {item.step}
                  </span>
                </div>
                <h3 className="font-semibold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {item.title}
                </h3>
                <p className="text-stone-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Trusted by Wellness Seekers
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="card-elevated p-6 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[hsl(43,70%,52%)] text-[hsl(43,70%,52%)]" />
                  ))}
                </div>
                <p className="text-stone-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-[hsl(120,10%,18%)]">{testimonial.name}</p>
                  <p className="text-sm text-stone-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Choose Your Plan
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Select the consultation level that best suits your needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "AI Ayurvedic Assessment",
                price: 1.49,
                originalPrice: 28,
                tag: "Quick",
                tagColor: "bg-blue-500",
                description: "AI-powered Ayurvedic assessment",
                disclaimer: "Not a comprehensive diagnosis or a substitute for professional healthcare advice.",
                features: ["Customised Report", "Modern wellness interpretation", "Lifestyle recommendations", "Herbal remedy suggestions", "Report sent to your email"]
              },
              {
                name: "Doctor-Reviewed",
                price: 37,
                originalPrice: 74,
                tag: "Safe",
                tagColor: "bg-emerald-500",
                description: "AI assessment reviewed by Ayurvedic doctor",
                disclaimer: "Non-clinical review. Does not replace medical advice.",
                features: ["Everything in AI Assessment", "Review by certified Vaidya", "Personalized doctor notes", "Verified report via email", "Priority support"],
                highlighted: true
              },
              {
                name: "1-on-1 Consultation",
                price: 45,
                originalPrice: 90,
                tag: "Value",
                tagColor: "bg-[hsl(22,70%,44%)]",
                description: "Complete assessment + video call",
                disclaimer: "Ayurvedic diet and lifestyle guidance only.",
                features: ["Everything in AI Assessment", "45-min video consultation", "Direct Q&A with doctor", "Report sent to your email", "Follow-up guidance"],
                includesAyushCare: true
              }
            ].map((plan, index) => (
              <div 
                key={index} 
                className={`card-elevated p-8 relative ${plan.highlighted ? 'border-2 border-[hsl(43,70%,52%)] scale-105' : ''}`}
              >
                <span className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${plan.tagColor} text-white text-xs px-3 py-1 rounded-full font-medium`}>
                  {plan.tag}
                </span>
                <h3 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-2 mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {plan.name}
                </h3>
                <p className="text-stone-600 text-sm mb-4">{plan.description}</p>
                <div className="mb-4">
                  {plan.originalPrice && (
                    <div className="mb-1">
                      <span className="text-lg text-stone-400 line-through">${plan.originalPrice}</span>
                      <span className="text-sm text-stone-400 ml-1">USD</span>
                    </div>
                  )}
                  <span className="text-4xl font-bold text-[hsl(22,70%,44%)]">${plan.price}</span>
                  <span className="text-stone-500 ml-1">USD</span>
                  <span className="text-stone-400 text-sm ml-1">one-time</span>
                  {plan.originalPrice && (
                    <span className="text-sm text-emerald-600 font-medium ml-2">{plan.price < 2 ? '95% OFF' : '50% OFF'}</span>
                  )}
                </div>
                <ul className="space-y-3 mb-6 pt-3 border-t border-stone-100">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                      <Check className="w-4 h-4 text-[hsl(135,15%,33%)]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {/* AYUSH CARE 15 - Only for consultation plan */}
                {plan.includesAyushCare && (
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-emerald-700 font-medium text-center">
                      🌿 Includes AYUSH CARE 15 — 15-Day Free Support Plan
                    </p>
                  </div>
                )}
                <Button 
                  onClick={login}
                  className={plan.highlighted ? "w-full btn-primary" : "w-full btn-secondary"}
                  data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="badge-primary mb-4">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Learn more about Ayurveda and how our consultation process works
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="item-1" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                What is Ayurveda?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Ayurveda is an ancient Indian medical system that focuses on achieving health and wellness by balancing the mind, body, spirit, and environment. It emphasizes prevention of illness and uses natural remedies, personalised to each individual's unique constitution, which includes their physical, mental, and spiritual makeup.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                How does Ayurveda work?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Ayurveda works by identifying and balancing the three energy types, or doshas, in your body: Vata, Pitta, and Kapha. Your treatment might include herbs, dietary adjustments, lifestyle changes, yoga, and meditation, which all help to restore balance and support your body's natural healing abilities. Ayurveda also includes detoxifying therapies like Panchakarma to help rejuvenate your body and boost your overall vitality.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                Is there scientific support for Ayurveda?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Yes, numerous studies and clinical trials, including those indexed by the National Library of Medicine, support the effectiveness of Ayurveda, especially in managing stress, improving digestion, reducing chronic pain, and enhancing mental health. Ayurveda's holistic approach to health has been validated by both historical use and modern research.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                Is Ayurveda safe and regulated?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Yes, Ayurveda is recognized and regulated by various health authorities worldwide. In India, the Ministry of AYUSH oversees Ayurvedic education, research, and practice. The World Health Organization (WHO) has published guidelines for traditional medicine including Ayurveda. In the USA, Ayurvedic products are regulated by the FDA as dietary supplements, while in Europe, the European Medicines Agency provides frameworks for traditional herbal medicines. Countries like Australia (TGA), Canada (Health Canada), and the UK (MHRA) have their own regulatory standards for complementary medicines. While generally safe under the guidance of a qualified practitioner, it's important to consult with a healthcare provider knowledgeable in Ayurveda to tailor treatments to your individual needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                Can Ayurveda be integrated with Western medicine?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Absolutely. Many people combine Ayurvedic practices with Western medicine to enhance their overall wellbeing. It's important to coordinate care between your Ayurvedic practitioner and discuss with your regular healthcare provider to safely integrate treatments.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                What does individualised treatment in Ayurveda mean?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Individualised treatment in Ayurveda means that your care plan is customized to your unique physical and health profile. Ayurveda looks at your specific needs, balancing any deficiencies or excesses through a carefully selected mix of treatments. This approach is particularly effective for preventive care and managing chronic conditions.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                How does Ayurveda address mental health?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Ayurveda approaches mental health by aiming to balance the mind and body through meditation, yoga, dietary changes, and specific treatments like Panchakarma. These practices help manage conditions ranging from stress and anxiety to more complex issues, improving overall mental wellbeing.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                Do I need to follow a strict diet and lifestyle with Ayurveda?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Not necessarily. While some adjustments to your diet and lifestyle may be recommended to enhance the effectiveness of Ayurvedic treatments, these changes are tailored to be manageable and sustainable, fitting into your daily life without significant disruption. The aim is to promote balance and health in a practical and supportive way.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                How does the AI consultation work?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Our AI-powered consultation analyzes your health concerns and lifestyle profile using advanced technology trained on Ayurvedic principles. You&apos;ll receive a personalized assessment identifying your dosha imbalances, along with tailored recommendations for diet, lifestyle, and herbal remedies. The report includes both traditional Ayurvedic insights and modern wellness interpretations. Note: This is for educational purposes only and is not a medical diagnosis.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-10" className="border border-stone-200 rounded-xl px-6 bg-stone-50/50">
              <AccordionTrigger className="text-left font-semibold text-[hsl(120,10%,18%)] hover:no-underline py-5">
                What&apos;s included in the consultation report?
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 pb-5">
                Your consultation report includes: a detailed Ayurvedic assessment based on your concerns and constitution, identification of dosha imbalances, personalized dietary recommendations, lifestyle do&apos;s and don&apos;ts, herbal remedy suggestions with proper warnings, and if recommended, Panchakarma treatment options. The report is downloadable as a PDF and emailed to you for future reference.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[hsl(22,70%,44%)] text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Begin Your Wellness Journey
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Get your personalized Ayurvedic consultation today. One-time fee, lifetime access to your report.
          </p>
          <Button 
            onClick={login}
            className="bg-white text-[hsl(22,70%,44%)] hover:bg-stone-100 rounded-full px-8 py-3 text-lg font-medium"
            data-testid="cta-start-btn"
          >
            Start Consultation - $1.49
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};
